import type {
  GetAllOpenQuestionAnswersResponse,
  GetImageSurveyResultResponse,
  GetSurveyResultResponse,
  GetUserQuestionAnswersRankingResponse,
  PostImageSurveyInteractiveQuestionRequestBody,
  PostOpenInteractiveQuestionAnswerRequestBody,
  PostScoreableInteractiveQuestionAnswerRequestBody,
  PostSurveyInteractiveQuestionRequestBody,
  PostWidgetInteractiveQuestionRequestBody,
  TrackingDataCommonResponse,
} from '@genially/contracts';
import { TrackingInteractiveQuestionType } from '@genially/contracts';
import type { GameStateModel } from '@genially/p2p-client';
import {
  type InteractiveQuestionState,
  isPlayerUserAnswerScoreable,
  type PlayerUserAnswer,
} from '@genially/p2p-lib';
import { UserTrackingData } from '@genially/ui/build/hooks/useFetchUserTrackingData/models/UserTrackingData';
import { processActivitiesDone } from '@genially/ui/build/utils/analytics/processActivitiesDone';
import { processHits } from '@genially/ui/build/utils/analytics/processHits';
import {
  processAttempts,
  processQuestions,
} from '@genially/ui/build/utils/analytics/processQuestions';

import { dataFormater } from '../../application/utils/dataFormater';
import { ViewApiService } from '../ViewApiService';

const areOpenAnswers = (
  answer: InteractiveQuestionState['playerUserAnswers'][number],
): answer is InteractiveQuestionState<
  PostOpenInteractiveQuestionAnswerRequestBody & { isHidden?: boolean }
>['playerUserAnswers'][number] => {
  return answer.answer.type === TrackingInteractiveQuestionType.OpenAnswer;
};

const NATIVE_SCOREABLE_TYPES = [
  TrackingInteractiveQuestionType.Quiz,
  TrackingInteractiveQuestionType.TrueFalse,
  TrackingInteractiveQuestionType.Image,
  TrackingInteractiveQuestionType.Sort,
  TrackingInteractiveQuestionType.FillInTheBlanks,
  TrackingInteractiveQuestionType.ShortAnswer,
];

const areScoreableAnswers = (
  answer: InteractiveQuestionState['playerUserAnswers'][number],
): answer is InteractiveQuestionState<
  | PostScoreableInteractiveQuestionAnswerRequestBody
  | PostWidgetInteractiveQuestionRequestBody
>['playerUserAnswers'][number] => {
  if (answer.answer.type === TrackingInteractiveQuestionType.Widget) {
    return (
      (answer.answer as PostWidgetInteractiveQuestionRequestBody).isScoreable === true
    );
  }
  return NATIVE_SCOREABLE_TYPES.includes(answer.answer.type);
};

const areSurveyAnswers = (
  answer: InteractiveQuestionState['playerUserAnswers'][number],
): answer is InteractiveQuestionState<
  PostSurveyInteractiveQuestionRequestBody | PostImageSurveyInteractiveQuestionRequestBody
>['playerUserAnswers'][number] => {
  return [
    TrackingInteractiveQuestionType.Survey,
    TrackingInteractiveQuestionType.ImageSurvey,
  ].includes(answer.answer.type);
};

type CategorizableWidgetPlayerUserAnswer =
  InteractiveQuestionState<PostWidgetInteractiveQuestionRequestBody>['playerUserAnswers'][number];

const areCategorizableWidgetAnswers = (
  answer: InteractiveQuestionState['playerUserAnswers'][number],
): answer is CategorizableWidgetPlayerUserAnswer => {
  return (
    answer.answer.type === TrackingInteractiveQuestionType.Widget &&
    (answer.answer as PostWidgetInteractiveQuestionRequestBody).isCategorizable === true
  );
};

export class PlayerAnswersInterpreter {
  private state: Map<string, InteractiveQuestionState>;

  private geniallyId: string;

  private constructor(geniallyId: string, state: Map<string, InteractiveQuestionState>) {
    this.geniallyId = geniallyId;
    this.state = state;
  }

  static from(geniallyId: string, game: GameStateModel) {
    return new PlayerAnswersInterpreter(
      geniallyId,
      new Map(Object.entries(game.asJS.interactiveQuestionAnswers)),
    );
  }

  interpretOpenAnswers(interactiveQuestionId: string): GetAllOpenQuestionAnswersResponse {
    const interactiveQuestionState = this.state.get(interactiveQuestionId);

    if (!interactiveQuestionState) {
      return [];
    }

    const processedAnswers = interactiveQuestionState.playerUserAnswers
      .filter(areOpenAnswers as any)
      .filter(({ answer }) => answer.interactiveQuestionId === interactiveQuestionId);

    processedAnswers.sort((a, b) => b.answer.timestamp - a.answer.timestamp);

    return processedAnswers.map(({ player, answer }) => ({
      geniallyUserId: '',
      userAlias: player.username,
      registeredAt: new Date(answer.timestamp).toISOString(),
      answerText: (answer as any).answerText,
      isHidden: answer.isHidden,
    }));
  }

  interpretSurvey(interactiveQuestionId: string): GetSurveyResultResponse {
    const interactiveQuestionState = this.state.get(interactiveQuestionId);

    if (!interactiveQuestionState) {
      return [];
    }

    const processedAnswers = interactiveQuestionState.playerUserAnswers
      .filter(areSurveyAnswers as any)
      .filter(({ answer }) => answer.interactiveQuestionId === interactiveQuestionId);

    const answers = processedAnswers.flatMap(i => (i.answer as any).answerIds);
    const totalAnswersPerAnswerId: { [key: string]: number } = {};

    answers.forEach(answerId => {
      if (totalAnswersPerAnswerId[answerId] !== undefined) {
        totalAnswersPerAnswerId[answerId] += 1;
      } else {
        totalAnswersPerAnswerId[answerId] = 1;
      }
    });

    return Object.entries(totalAnswersPerAnswerId).map(([answerId, totalAnswers]) => ({
      geniallyId: this.geniallyId,
      interactiveQuestionId,
      answerId,
      totalAnswers,
    }));
  }

  interpretCategorizableWidget(interactiveQuestionId: string): GetSurveyResultResponse {
    const interactiveQuestionState = this.state.get(interactiveQuestionId);

    if (!interactiveQuestionState) {
      return [];
    }

    const processedAnswers = interactiveQuestionState.playerUserAnswers
      .filter(areCategorizableWidgetAnswers)
      .filter(({ answer }) => answer.interactiveQuestionId === interactiveQuestionId);

    const groupedAnswers: {
      [key: string]: { totalAnswers: number; score?: { value: number; max: number } };
    } = {};

    processedAnswers.forEach(({ answer }) => {
      const { userAnswer } = answer;
      const { score, answerText } = userAnswer ?? {};

      // Group by the displayed option label: the numeric `score.value` when
      // present (it supports averaging downstream), the free-text `answerText`
      // otherwise. The key is only a label; the `score` itself is kept in the
      // group, so it never needs to be parsed back from the key.
      const answerId = score !== undefined ? String(score.value) : answerText;

      if (answerId === undefined) {
        return;
      }

      if (groupedAnswers[answerId] === undefined) {
        groupedAnswers[answerId] = { totalAnswers: 0 };
      }

      // If the rating scale was changed mid-session, the same value can arrive
      // with different maxes; keep the largest so the reported scale stays
      // consistent with the `Math.max` used downstream.
      const group = groupedAnswers[answerId];
      if (
        score !== undefined &&
        (group.score === undefined || score.max > group.score.max)
      ) {
        group.score = score;
      }

      group.totalAnswers += 1;
    });

    return Object.entries(groupedAnswers).map(([answerId, { totalAnswers, score }]) => ({
      geniallyId: this.geniallyId,
      interactiveQuestionId,
      answerId,
      totalAnswers,
      ...(score !== undefined ? { score } : {}),
    }));
  }

  interpretImageSurvey(interactiveQuestionId: string): GetImageSurveyResultResponse {
    const interactiveQuestionState = this.state.get(interactiveQuestionId);

    if (!interactiveQuestionState) {
      return [];
    }

    const processedAnswers = interactiveQuestionState.playerUserAnswers
      .filter(areSurveyAnswers)
      .filter(({ answer }) => answer.interactiveQuestionId === interactiveQuestionId);

    const answers = processedAnswers.flatMap(i => i.answer.answerIds);
    const totalAnswersPerAnswerId: { [key: string]: number } = {};

    answers.forEach(answerId => {
      if (totalAnswersPerAnswerId[answerId] !== undefined) {
        totalAnswersPerAnswerId[answerId] += 1;
      } else {
        totalAnswersPerAnswerId[answerId] = 1;
      }
    });

    return Object.entries(totalAnswersPerAnswerId).map(([answerId, totalAnswers]) => ({
      geniallyId: this.geniallyId,
      interactiveQuestionId,
      answerId,
      totalAnswers,
    }));
  }

  interpretRanking(): GetUserQuestionAnswersRankingResponse {
    const allScoreableAnswers = Array.from(this.state.values())
      .flatMap(i => i.playerUserAnswers)
      .filter(areScoreableAnswers);

    // Grouped by the player's stable id, not their username: a player's alias
    // can change mid-session (claiming an alias after answering anonymously),
    // and answers keep whatever username was current when they were sent —
    // grouping by that snapshot would split one player into a row per alias.
    const uniqueScoreableAnswersPerPlayerId: Record<
      string,
      {
        username: string;
        latestUsernameTimestamp: number;
        answers: PlayerUserAnswer<
          | PostScoreableInteractiveQuestionAnswerRequestBody
          | PostWidgetInteractiveQuestionRequestBody
        >[];
      }
    > = {};

    for (const answer of allScoreableAnswers) {
      const {
        player: { id, username },
        answer: answerData,
      } = answer;

      let playerEntry = uniqueScoreableAnswersPerPlayerId[id];

      if (playerEntry === undefined) {
        playerEntry = {
          username,
          latestUsernameTimestamp: answerData.timestamp,
          answers: [],
        };
        uniqueScoreableAnswersPerPlayerId[id] = playerEntry;
      } else if (answerData.timestamp >= playerEntry.latestUsernameTimestamp) {
        playerEntry.username = username;
        playerEntry.latestUsernameTimestamp = answerData.timestamp;
      }

      const alreadyAnswered = playerEntry.answers.find(
        i => i.answer.interactiveQuestionId === answerData.interactiveQuestionId,
      )?.answer;

      // Get answer with the biggest time taken to answer
      if (
        alreadyAnswered === undefined ||
        alreadyAnswered.timestamp < answerData.timestamp
      ) {
        playerEntry.answers.push(answer);
      }
    }

    const rankingData = Object.values(uniqueScoreableAnswersPerPlayerId).map(
      ({ username, answers }) => ({
        username,
        timeTakenToAnswer: answers.reduce(
          (acc, i) => acc + i.answer.timeTakenToAnswer,
          0,
        ),
        correctAnswers: answers.filter(i => i.isCorrect).length,
        wrongAnswers: answers.filter(i => !i.isCorrect).length,
        timestamp: Math.max(...answers.map(i => i.answer.timestamp)),
      }),
    );

    // The order of what thing is more important to the ranking is
    // right answers > wrong answers > time taken to answer > last answer registered at

    const sortedRankingData = rankingData.sort((a, b) => {
      if (a.correctAnswers !== b.correctAnswers) {
        return b.correctAnswers - a.correctAnswers;
      }

      if (a.wrongAnswers !== b.wrongAnswers) {
        return b.wrongAnswers - a.wrongAnswers;
      }

      if (a.timeTakenToAnswer !== b.timeTakenToAnswer) {
        return a.timeTakenToAnswer - b.timeTakenToAnswer;
      }

      return a.timestamp - b.timestamp;
    });

    const response: GetUserQuestionAnswersRankingResponse = sortedRankingData.map(
      rankingItem => {
        return {
          userAlias: rankingItem.username,
          geniallyUserId: '',
          rightAnswers: rankingItem.correctAnswers,
          wrongAnswers: rankingItem.wrongAnswers,
          timeTakenToAnswerSum: rankingItem.timeTakenToAnswer,
          lastAnswerRegisteredAt: new Date(rankingItem.timestamp).toISOString(),
        };
      },
    );

    return response;
  }

  async interpretUserTrackingData(): Promise<UserTrackingData[]> {
    const geniallyData = await ViewApiService.getViewData(this.geniallyId);
    const geniallyContent = dataFormater(geniallyData);

    // Grouped by the player's stable id, not their username — see the same
    // note in interpretRanking above.
    const answersPerUser: Map<string, TrackingDataCommonResponse> = new Map();
    const latestUsernameTimestampByPlayerId: Map<string, number> = new Map();

    Array.from(this.state.values())
      .flatMap(i => i.playerUserAnswers)
      .forEach(i => {
        // `isCorrect` sits next to `answer` (not inside it) on PlayerUserAnswer,
        // but processQuestions' Random handling has no document-resident answer
        // key to recompute correctness from (it lives in the bank), so it needs
        // this flag carried on the answer object itself, same as the other
        // Play-only field (`bankInteractiveQuestionId`) already riding along.
        const answer = isPlayerUserAnswerScoreable(i)
          ? { ...i.answer, isCorrect: i.isCorrect }
          : i.answer;

        const { id, username } = i.player;
        const trackingResponse = answersPerUser.get(id);
        const latestUsernameTimestamp = latestUsernameTimestampByPlayerId.get(id);

        if (trackingResponse) {
          trackingResponse.answers.push(answer);

          if (
            latestUsernameTimestamp === undefined ||
            i.answer.timestamp >= latestUsernameTimestamp
          ) {
            trackingResponse.userAlias = username;
            latestUsernameTimestampByPlayerId.set(id, i.answer.timestamp);
          }
        } else {
          const data: TrackingDataCommonResponse = {
            answers: [answer],
            userAlias: username,
            firstSessionCreatedAt: 0,
            numberOfSessions: 1,
            geniallyId: '',
            slidesVisited: 0,
            totalDuration: 0,
          };

          answersPerUser.set(id, data);
          latestUsernameTimestampByPlayerId.set(id, i.answer.timestamp);
        }
      });

    const trackingCommonResponse = Array.from(answersPerUser.values());

    const response = trackingCommonResponse.map(userData => {
      const questions = processQuestions(userData, geniallyContent, true);
      const questionsAttempts = processAttempts(userData, geniallyContent);
      const activitiesDone = processActivitiesDone(userData, geniallyContent);
      const hits = processHits(questions);

      return UserTrackingData.of({
        userAlias: userData.userAlias,
        questions,
        questionsAttempts,
        activitiesDone,
        hits,
        totalAccess: 0,
        sessionDuration: 0,
        slidesVisited: 0,
        visitedSlidesAmountOverTotalSlides: '',
        firstAccessDate: new Date(),
      });
    });

    return response;
  }
}
